import { createAsyncThunk } from '@reduxjs/toolkit';
import { getFirebaseBackend } from "../../../helpers/firebase_helper";
import { setOriginalUser } from '../impersonate/thunk';
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import 'firebase/compat/firestore';
import { loginSuccess, logoutUserSuccess, apiError, reset_login_flag } from './reducer';
import { NavigateFunction } from 'react-router-dom';

// Add a new piece of state to track impersonation
let originalUser: any = null;

// Interface for login history
interface LoginRecord {
  timestamp: firebase.firestore.Timestamp;
  ipAddress: string;
  userId: string;
  userAgent: string;
  loginMethod: string;
}

// Function to get client IP address
const getClientIP = async (): Promise<string> => {
  try {
    const response = await fetch('https://api.ipify.org?format=json');
    const data = await response.json();
    return data.ip;
  } catch (error) {
    console.error('Error fetching IP:', error);
    return 'unknown';
  }
};

// Function to update user record in Firestore
const updateUserRecord = async (userData: any, loginMethod: string) => {
  const firestore = firebase.firestore();
  const ipAddress = await getClientIP();

  try {
    // Prepare user data
    const userDoc = {
      uid: userData.uid,
      email: userData.email,
      displayName: userData.displayName,
      photoURL: userData.photoURL,
      phoneNumber: userData.phoneNumber,
      lastLoginAt: firebase.firestore.FieldValue.serverTimestamp(),
      createdAt: firebase.firestore.FieldValue.serverTimestamp(), // Will only be set on first creation
      updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
      // Include all other fields from GlobalUserHooks
      userName: userData.displayName || '',
      isContractor: !!sessionStorage.getItem('impersonatedUser'),
      currentUserId: userData.uid,
    };

    // Create login record
    const loginRecord: LoginRecord = {
      timestamp: firebase.firestore.Timestamp.now(),
      ipAddress,
      userId: userData.uid,
      userAgent: navigator.userAgent,
      loginMethod
    };

    // Start a batch write
    const batch = firestore.batch();

    // Update or create user document
    const userRef = firestore.collection('accounts').doc(userData.uid);
    batch.set(userRef, userDoc, { merge: true });

    // Add login record
    const loginRef = userRef.collection('logins').doc();
    batch.set(loginRef, loginRecord);

    // Commit the batch
    await batch.commit();

  } catch (error) {
    console.error('Error updating user record:', error);
    throw error;
  }
};

export interface LoginUserData {
  email: string;
  password: string;
  navigate?: NavigateFunction;
}

export const loginUser = createAsyncThunk<any, LoginUserData, { rejectValue: string }>(
  'auth/login',
  async ({ email, password, navigate }: LoginUserData, { rejectWithValue }) => {
    try {
      let response;

      if (process.env.REACT_APP_DEFAULTAUTH === "firebase") {
        const fireBaseBackend = getFirebaseBackend();
        
        if (!fireBaseBackend) {
          throw new Error('Firebase backend not initialized');
        }

        response = await fireBaseBackend.loginUser(email, password);
        if (response) {
          if (navigate) {
            // Get the return URL from sessionStorage or default to dashboard
            const returnUrl = sessionStorage.getItem('returnUrl') || '/dashboard';
            // Clear the stored return URL
            sessionStorage.removeItem('returnUrl');
            // Navigate to the return URL
            navigate(returnUrl);
          }
          return response;
        } else {
          return rejectWithValue('Login failed');
        }
      }
      // ... rest of the auth methods remain the same
    } catch (error: any) {
      const errorMessage = typeof error === 'string' ? error : error.message || 'Login failed';
      return rejectWithValue(errorMessage);
    }
  }
);

export const resetLoginFlag = () => async (dispatch: any) => {
  try {
    const response = await dispatch({ type: 'auth/resetLoginFlag' });
    return response;
  } catch (error) {
    return error;
  }
};

export const socialLogin = (type: any, history: any) => async (dispatch: any) => {
  try {
    let response;

    if (process.env.REACT_APP_DEFAULTAUTH === "firebase") {
      const fireBaseBackend: any = getFirebaseBackend();
      response = await fireBaseBackend.socialLoginUser(type);
    }

    const socialData = await response;

    if (socialData) {
      await updateUserRecord(socialData, `social_${type}`);
      var socialDataJson = JSON.stringify(socialData);
      sessionStorage.setItem("authUser", socialDataJson);
      dispatch(loginSuccess(socialDataJson));
      
      // Get the return URL from sessionStorage or default to dashboard
      const returnUrl = sessionStorage.getItem('returnUrl') || '/dashboard';
      // Clear the stored return URL
      sessionStorage.removeItem('returnUrl');
      // Navigate to the return URL
      history(returnUrl);
    }

  } catch (error: any) {
    dispatch(apiError(error));
  }
};

export const logoutUser = () => async (dispatch: any) => {
  try {
    const fireBaseBackend = getFirebaseBackend();
    
    if (process.env.REACT_APP_DEFAULTAUTH === "firebase") {
      // First perform the logout operation
      await fireBaseBackend?.logout();
    }
    
    // Clear session storage
    sessionStorage.removeItem("authUser");
    
    // Dispatch success action without passing response
    dispatch(logoutUserSuccess());
    
  } catch (error: any) {
    dispatch(apiError(error));
  }
};

